// Mantine hooks
import { useLocalStorage } from '@mantine/hooks'

// React
import { useEffect } from 'react'

// API
import useApiSWR from '@/hooks/useApiSWR'

// User
import useMe from '@/hooks/useMe'
import { nfcReadersSelectItems, normalizeNfcSelection } from '@/lib/nfc'

const legacyMapping = {
    1: {
        locationId: 2,
        printerId: 3,
    },
    2: {
        locationId: 3,
    },
    3: {
        locationId: 1,
        printerId: 1,
    },
    4: {
        locationId: 1,
        printerId: 2,
    },
    5: {
        locationId: 1,
        printerId: 4,
    },
    6: {
        locationId: 6,
    },
    7: {
        locationId: 7,
    },
}

export default function useUserLocation() {
    // User
    const { isAdmin } = useMe()

    // Get all possible locations. Revalidated on focus, unlike the other
    // reference lists: the printer states ride along with them.
    const { data: locations = [] } = useApiSWR('/locations', null, {
        revalidateOnFocus: true,
        revalidateOnReconnect: true,
    })
    // Format for Mantine Select
    const locationsSelectItems =
        locations?.map((location) => ({
            value: String(location.id),
            label: location.name,
        })) || []

    // Current Location ID from localStorage
    const [userLocationIdLocalStorage, setUserLocationId] = useLocalStorage({
        key: 'user_location_id',
        defaultValue: 1,
    })

    const userLocationId = isAdmin ? Number(userLocationIdLocalStorage) : 1

    const onUserLocationSelect = (value) => setUserLocationId(Number(value))

    // Get user location
    const userLocation = locations.find(
        (location) => location.id === userLocationId
    )

    // List printers and order them by id ascending
    const printers =
        locations
            .flatMap((location) => location.location_printers)
            .sort((a, b) => a.id - b.id) || []

    // Printer types config
    const printerTypes = [
        { key: 'shipping_label', label: 'Thermique Étiquettes Transport' },
        { key: 'product_label', label: 'Thermique Étiquettes Produits' },
        { key: 'a4', label: 'Imprimante A4' },
    ]

    // Migrate old user_printer_id → user_printer_shipping_label
    useEffect(() => {
        const oldValue = localStorage.getItem('user_printer_id')
        if (oldValue && !localStorage.getItem('user_printer_shipping_label')) {
            localStorage.setItem('user_printer_shipping_label', oldValue)
            localStorage.removeItem('user_printer_id')
        }
        // Old NFC toggle, replaced by user_nfc_reader selection
        localStorage.removeItem('nfc_scanner_enabled')
    }, [])

    // One localStorage key per printer type
    const [shippingLabelId, setShippingLabelId, removeShippingLabelId] =
        useLocalStorage({ key: 'user_printer_shipping_label' })
    const [productLabelId, setProductLabelId, removeProductLabelId] =
        useLocalStorage({ key: 'user_printer_product_label' })
    const [a4Id, setA4Id, removeA4Id] =
        useLocalStorage({ key: 'user_printer_a4' })

    // Normalize legacy workstation labels to a stable server/port selection.
    const [storedNfcReader, setNfcReader, removeNfcReader] = useLocalStorage({
        key: 'user_nfc_reader',
    })
    const nfcReader = normalizeNfcSelection(storedNfcReader)

    const onNfcReaderSelect = (value) => {
        if (value && value !== 'none') {
            setNfcReader(value)
        } else {
            removeNfcReader()
        }
    }

    const userPrinterIds = {
        shipping_label: shippingLabelId,
        product_label: productLabelId,
        a4: a4Id,
    }

    const printerSetters = {
        shipping_label: { set: setShippingLabelId, remove: removeShippingLabelId },
        product_label: { set: setProductLabelId, remove: removeProductLabelId },
        a4: { set: setA4Id, remove: removeA4Id },
    }

    const onPrinterSelect = (type, value) => {
        if (value && value !== 'none') {
            printerSetters[type].set(Number(value))
        } else {
            printerSetters[type].remove()
        }
    }

    // Build select items per type
    const getPrintersSelectItems = (type) => [
        { value: 'none', label: "Pas d'imprimante" },
        ...printers
            .filter((p) => p.type === type)
            .map((printer) => ({
                value: String(printer.id),
                label: printer.name,
                state: printer.state,
                at_current_location: printer.location_id === userLocationId,
            })),
    ]

    // Get printer objects per type
    const userPrinters = {
        shipping_label: printers.find((p) => p.id === shippingLabelId),
        product_label: printers.find((p) => p.id === productLabelId),
        a4: printers.find((p) => p.id === a4Id),
    }

    // Legacy compat: userPrinter = shipping_label (used for shipping labels)
    const userPrinter = userPrinters.shipping_label
    const userPrinterId = shippingLabelId

    // Get last check (max value of last_check_at)
    const lastPrintersCheck = printers.reduce((max, printer) => {
        const currentDate = new Date(printer.last_check_at)
        return currentDate > max ? currentDate : max
    }, new Date(0))

    /*
        Legacy location handler
    */
    const [legacyLocation, setLegacyLocation, removeLegacyLocation] =
        useLocalStorage({
            key: 'current_location',
        })

    // Set new values
    useEffect(() => {
        if (legacyLocation) {
            // Get legacy location data
            const legacyLocationData = legacyMapping[Number(legacyLocation)]

            // Set new value according to legacy mapping
            if (legacyLocationData?.locationId) {
                setUserLocationId(legacyLocationData.locationId)

                if (legacyLocationData?.printerId) {
                    setShippingLabelId(legacyLocationData.printerId)
                } else {
                    removeShippingLabelId()
                }

                // Remove legacy location
                removeLegacyLocation()
            }
        }
    }, [legacyLocation])

    return {
        locations,
        locationsSelectItems,
        onUserLocationSelect,
        userLocationId,
        userLocation,
        // Per-type printer selection
        printerTypes,
        userPrinters,
        userPrinterIds,
        onPrinterSelect,
        getPrintersSelectItems,
        lastPrintersCheck,
        printers,
        // Legacy compat (shipping labels)
        userPrinter,
        userPrinterId,
        // NFC reader
        nfcReader,
        onNfcReaderSelect,
        nfcReadersSelectItems,
    }
}
